import { useEffect, useState } from "react"
import { adminApi, type Category } from "@/lib/admin-api"
import { Card, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from "@/components/ui/table"
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from "@/components/ui/dialog"
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from "@/components/ui/dropdown-menu"
import { Plus, Search, MoreVertical, Pencil, Trash2, FolderTree, Loader2, Upload, X } from "lucide-react"
import { toast } from "sonner"

export default function AdminCategories() {
    const [categories, setCategories] = useState<Category[]>([])
    const [isLoading, setIsLoading] = useState(true)
    const [searchQuery, setSearchQuery] = useState("")

    const [formModal, setFormModal] = useState<{ open: boolean; category: Category | null }>({
        open: false,
        category: null
    })
    const [deleteModal, setDeleteModal] = useState<{ open: boolean; category: Category | null }>({
        open: false,
        category: null
    })

    const [formData, setFormData] = useState({ name: "", description: "" })
    const [imageFile, setImageFile] = useState<File | null>(null)
    const [imagePreview, setImagePreview] = useState<string | null>(null)
    const [isSaving, setIsSaving] = useState(false)
    const [isDeleting, setIsDeleting] = useState(false)

    const fetchCategories = async () => {
        try {
            setIsLoading(true)
            const data = await adminApi.getCategories()
            setCategories(data)
        } catch (error) {
            toast.error("Kategoriyalarni yuklashda xatolik")
        } finally {
            setIsLoading(false)
        }
    }

    useEffect(() => {
        fetchCategories()
    }, [])

    const openFormModal = (category: Category | null = null) => {
        if (category) {
            setFormData({ name: category.name, description: category.description || "" })
            setImagePreview(category.image_url)
        } else {
            setFormData({ name: "", description: "" })
            setImagePreview(null)
        }
        setImageFile(null)
        setFormModal({ open: true, category })
    }

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (file) {
            setImageFile(file)
            setImagePreview(URL.createObjectURL(file))
        }
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!formData.name) {
            toast.error("Kategoriya nomini kiriting")
            return
        }

        setIsSaving(true)
        try {
            const form = new FormData()
            form.append("name", formData.name)
            form.append("description", formData.description)
            if (imageFile) {
                form.append("image", imageFile)
            }

            if (formModal.category) {
                await adminApi.updateCategory(formModal.category.id, form)
                toast.success("Kategoriya yangilandi")
            } else {
                await adminApi.createCategory(form)
                toast.success("Kategoriya yaratildi")
            }
            setFormModal({ open: false, category: null })
            fetchCategories()
        } catch (error: any) {
            toast.error(error.message || "Xatolik yuz berdi")
        } finally {
            setIsSaving(false)
        }
    }

    const handleDelete = async () => {
        if (!deleteModal.category) return
        setIsDeleting(true)
        try {
            await adminApi.deleteCategory(deleteModal.category.id)
            toast.success("Kategoriya o'chirildi")
            setDeleteModal({ open: false, category: null })
            fetchCategories()
        } catch (error) {
            toast.error("O'chirishda xatolik")
        } finally {
            setIsDeleting(false)
        }
    }

    const filteredCategories = categories.filter(c =>
        c.name.toLowerCase().includes(searchQuery.toLowerCase())
    )

    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-[60vh]">
                <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
            </div>
        )
    }

    return (
        <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold">Kategoriyalar</h1>
                    <p className="text-muted-foreground">Jami {categories.length} ta kategoriya</p>
                </div>
                <Button onClick={() => openFormModal()} className="bg-gradient-to-r from-emerald-500 to-teal-600 gap-2">
                    <Plus className="h-5 w-5" />
                    Yangi kategoriya
                </Button>
            </div>

            <Card>
                <CardContent className="p-4">
                    <div className="relative">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                        <Input
                            placeholder="Kategoriya qidirish..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="pl-12 h-12 bg-slate-50 dark:bg-gray-800 border-0"
                        />
                    </div>
                </CardContent>
            </Card>

            <Card>
                <CardContent className="p-0">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Kategoriya</TableHead>
                                <TableHead>Mahsulotlar</TableHead>
                                <TableHead className="w-[100px]"></TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {filteredCategories.map((category) => (
                                <TableRow key={category.id}>
                                    <TableCell>
                                        <div className="flex items-center gap-3">
                                            {category.image_url ? (
                                                <img src={category.image_url} alt={category.name} className="w-12 h-12 rounded-lg object-cover" />
                                            ) : (
                                                <div className="w-12 h-12 rounded-lg bg-slate-100 dark:bg-gray-800 flex items-center justify-center">
                                                    <FolderTree className="h-6 w-6 text-muted-foreground" />
                                                </div>
                                            )}
                                            <div>
                                                <p className="font-medium">{category.name}</p>
                                                <p className="text-sm text-muted-foreground truncate max-w-[300px]">{category.description}</p>
                                            </div>
                                        </div>
                                    </TableCell>
                                    <TableCell>{category.products_count} ta</TableCell>
                                    <TableCell>
                                        <DropdownMenu>
                                            <DropdownMenuTrigger asChild>
                                                <Button variant="ghost" size="icon"><MoreVertical className="h-4 w-4" /></Button>
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent align="end">
                                                <DropdownMenuItem onClick={() => openFormModal(category)}>
                                                    <Pencil className="h-4 w-4 mr-2" />Tahrirlash
                                                </DropdownMenuItem>
                                                <DropdownMenuItem onClick={() => setDeleteModal({ open: true, category })} className="text-red-600">
                                                    <Trash2 className="h-4 w-4 mr-2" />O'chirish
                                                </DropdownMenuItem>
                                            </DropdownMenuContent>
                                        </DropdownMenu>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    {filteredCategories.length === 0 && (
                        <div className="text-center py-12">
                            <FolderTree className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                            <p className="text-muted-foreground">Kategoriyalar topilmadi</p>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Form Modal */}
            <Dialog open={formModal.open} onOpenChange={(open) => setFormModal({ open, category: null })}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>{formModal.category ? "Kategoriyani tahrirlash" : "Yangi kategoriya"}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="space-y-2">
                            <Label>Nomi *</Label>
                            <Input
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                placeholder="Kategoriya nomi"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Tavsif</Label>
                            <Textarea
                                value={formData.description}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                placeholder="Qisqacha tavsif"
                                rows={3}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Rasm</Label>
                            {imagePreview ? (
                                <div className="relative">
                                    <img src={imagePreview} alt="Preview" className="w-full h-40 object-cover rounded-lg" />
                                    <Button type="button" variant="destructive" size="icon" className="absolute top-2 right-2"
                                        onClick={() => { setImageFile(null); setImagePreview(null) }}>
                                        <X className="h-4 w-4" />
                                    </Button>
                                </div>
                            ) : (
                                <label className="flex flex-col items-center justify-center h-32 border-2 border-dashed rounded-lg cursor-pointer hover:border-emerald-500">
                                    <Upload className="h-8 w-8 text-muted-foreground mb-2" />
                                    <span className="text-sm text-muted-foreground">Rasm yuklash</span>
                                    <input type="file" accept="image/*" onChange={handleImageChange} className="hidden" />
                                </label>
                            )}
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setFormModal({ open: false, category: null })}>Bekor</Button>
                            <Button type="submit" disabled={isSaving} className="bg-emerald-500 hover:bg-emerald-600">
                                {isSaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                                Saqlash
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Delete Modal */}
            <Dialog open={deleteModal.open} onOpenChange={(open) => setDeleteModal({ open, category: null })}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Kategoriyani o'chirish</DialogTitle>
                        <DialogDescription>"{deleteModal.category?.name}" ni o'chirmoqchimisiz?</DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDeleteModal({ open: false, category: null })}>Bekor</Button>
                        <Button variant="destructive" onClick={handleDelete} disabled={isDeleting}>
                            {isDeleting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}O'chirish
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    )
}
